import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  signal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { PaymentDetails } from "src/app/pages/shared/models/paymentdetails";
import { PaymentService } from "src/app/pages/shared/services/payment.service";
import { PaymentRequestModel } from "../../card-data-capture/card-processing-model";
import { customCreditCardValidatorValidator } from "../../card.util";
import { ExpiryDateFormatterPipe } from "../../expiry-date-formatter.pipe";
import { CheckoutSettings, WhiteLabelInfo } from "src/app/core/models/model";
import { map, timer } from "rxjs";
import { StorageService } from "src/app/core/helpers/storage-Service";

@Component({
  selector: "app-card-data-view",
  templateUrl: "./card-data-view.component.html",
  styleUrls: ["./card-data-view.component.css"],
  standalone: false,
})
export class CardDataViewComponent implements OnInit {
  @Input() paymentDetails: PaymentDetails;
  @Output() paymentData = new EventEmitter<PaymentRequestModel>();

  paymentRequest = new PaymentRequestModel();
  cardForm: UntypedFormGroup;

  timeoutSignal = toSignal(timer(3000).pipe(map(() => false)), {
    initialValue: true,
  });
  addCardClicked = signal(false);

  @ViewChild("ccNumber", { static: false }) ccNumberField: ElementRef;

  failedResponse: boolean;
  cardType: string;

  @Input() whiteLabelInfo: WhiteLabelInfo;
  checkoutSettings: CheckoutSettings;

  constructor(
    private fb: UntypedFormBuilder,
    private expdate: ExpiryDateFormatterPipe,
    private paymentService: PaymentService,
    private storageService: StorageService,
  ) {
    this.initCardForm();
  }

  ngOnInit() {
    this.initializeCardList();
  }

  async initializeCardList() {
    const cards = [
      {
        srcDigitalCardId: "card_abc123xyz",
        panLastFour: "4532",
        paymentCardType: "CREDIT",
        digitalCardData: {
          descriptorName: "Chase Sapphire",
          presentationName: "Chase Sapphire Preferred",
          status: "ACTIVE",
          artUri:
            "https://sbx.assets.mastercard.com/card-art/combined-image-asset/c911d734-2f4b-4326-8363-afb0ffe2bc2b.png",
        },
      },
      // {
      // 	srcDigitalCardId: "card_def456uvw",
      // 	panLastFour: "8765",
      // 	paymentCardType: "DEBIT",
      // 	digitalCardData: {
      // 		descriptorName: "Bank of America",
      // 		presentationName: "BofA Debit Card",
      // 		status: "ACTIVE",
      // 		artUri: "https://example.com/card-art/bofa-debit.png"
      // 	},
      // 	digitalCardFeatures: []
      // },
      // {
      // 	srcDigitalCardId: "card_ghi789rst",
      // 	panLastFour: "3210",
      // 	paymentCardType: "CREDIT",
      // 	digitalCardData: {
      // 		descriptorName: "American Express",
      // 		presentationName: "Amex Gold Card",
      // 		status: "PENDING",
      // 		artUri: "https://example.com/card-art/amex-gold.png"
      // 	},
      // 	digitalCardFeatures: [
      // 		{
      // 			cardFeature: "REWARDS",
      // 			content: "Earn 4x points on dining and groceries"
      // 		}
      // 	]
      // },
      // {
      // 	srcDigitalCardId: "card_jkl012mno",
      // 	panLastFour: "9876",
      // 	paymentCardType: "PREPAID",
      // 	digitalCardData: {
      // 		descriptorName: "Visa Prepaid",
      // 		presentationName: "Visa Gift Card",
      // 		status: "EXPIRED",
      // 		artUri: "https://example.com/card-art/visa-prepaid.png"
      // 	},
      // 	digitalCardFeatures: []
      // },
      // {
      // 	srcDigitalCardId: "card_pqr345stu",
      // 	panLastFour: "5555",
      // 	paymentCardType: "CREDIT",
      // 	digitalCardData: {
      // 		descriptorName: "Citi",
      // 		presentationName: "Citi Double Cash",
      // 		status: "SUSPENDED",
      // 		artUri: "https://example.com/card-art/citi-double.png"
      // 	},
      // 	digitalCardFeatures: [
      // 		{
      // 			cardFeature: "CASHBACK",
      // 			content: "2% cash back on all purchases"
      // 		}
      // 	]
      // }
    ];

    await new Promise((resolve) => setTimeout(resolve, 4000));

    const srcCardList = document.getElementById("srcCardList") as any;
    // console.log(srcCardList)

    if (!srcCardList) return;

    (srcCardList as any).loadCards(cards);

    // Listen for events
    srcCardList.addEventListener("selectSrcDigitalCardId", (event) => {
      this.addCardClicked.set(false);
    });

    srcCardList.addEventListener("clickAddCardLink", () => {
      this.addCardClicked.set(true);
    });
  }

  validateInput(evt) {
    return evt.charCode >= 43 && evt.charCode <= 57;
  }

  logActivity(value: string, type?: string): string {
    if (value.startsWith("4")) {
      this.cardType = "visa";
    } else if (value.startsWith("5")) {
      if (
        value.startsWith("5061") ||
        value.startsWith("5062") ||
        value.startsWith("5063") ||
        value.startsWith("5064") ||
        value.startsWith("5065")
      ) {
        this.cardType = "verve";
      } else {
        this.cardType = "mastercard";
      }
    } else {
      return (this.cardType = "");
    }
  }

  initCardForm() {
    this.cardForm = this.fb.group({
      authDataVersion: [1],
      pan: [
        "",
        {
          validator: [
            Validators.required,
            customCreditCardValidatorValidator(),
          ],
          updatedOn: blur,
        },
      ],
      expiryDate: [],
      cvv2: [""],
      pin: [""],
    });
  }

  formatExpDate(exp, event?: KeyboardEvent) {
    const isBackspace = event?.key === "Backspace";
    let value: string = "";
    value = value.replace(/[^0-9/]/g, "");
    if (!isBackspace) {
      value = this.expdate.transform(exp);
      this.cardForm.patchValue({ expiryDate: value });
    }
  }

  sendTxnData() {
    this.paymentData.emit(this.cardForm.value);
  }

  get f() {
    return this.cardForm.controls;
  }

  /* Insert spaces to make CC number more legible */
  cardNumberSpacing() {
    const input = this.ccNumberField.nativeElement;
    const { selectionStart } = input;
    const { pan } = this.cardForm.controls;

    let trimmedCardNum = pan.value.replace(/\s+/g, "");

    if (trimmedCardNum.length > 19) {
      trimmedCardNum = trimmedCardNum.substr(0, 19);
    }

    /* Handle American Express 4-6-5 spacing format */
    const partitions =
      trimmedCardNum.startsWith("34") || trimmedCardNum.startsWith("37")
        ? [4, 6, 5]
        : [4, 4, 4, 4];

    const numbers = [];
    let position = 0;
    partitions.forEach((partition) => {
      const part = trimmedCardNum.substr(position, partition);
      if (part) numbers.push(part);
      position += partition;
    });

    pan.setValue(numbers.join(" "));

    /* Handle caret position if user edits the number later */
    if (selectionStart < pan.value.length - 1) {
      input.setSelectionRange(selectionStart, selectionStart, "none");
    }
  }

  moveToNext(
    event: Event,
    nextInput: HTMLInputElement,
    type: "card" | "expiry" | "cvv",
  ) {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, ""); // remove spaces or non-digits

    if (type === "card") {
      const requiredLength = this.getCardLength(value);

      // Only move when card digits are EXACTLY the expected length
      if (value.length === requiredLength) {
        nextInput.focus();
      }
      return;
    }

    if (type === "expiry") {
      // expiry format MM/YY -> total 5 characters with slash
      if (input.value.length === 5) {
        nextInput.focus();
      }
      return;
    }

    if (type === "cvv") {
      if (value.length === 3) {
        nextInput.focus();
      }
      return;
    }
  }

  /** Detect card type and return the correct required length */
  private getCardLength(value: string): number {
    if (!value) return 16;

    // Verve cards typically start with 506, 507, or 650...push to prod
    if (/^(506|507|650)/.test(value)) {
      return 19; // Verve always 19 digits
    }

    // Visa always starts with '4'
    if (/^4/.test(value)) {
      return 16;
    }

    // Mastercard starts with 51-55 or 2221-2720
    if (/^(5[1-5]|222[1-9]|22[3-9]\d|2[3-6]\d{2}|27[01]\d|2720)/.test(value)) {
      return 16;
    }

    // Default fallback (Visa/Mastercard standard)
    return 16;
  }
}
